/**
 * AssetUploader — A3 Branding (Sprint Frontend Brief 5 · Tela 4).
 *
 * Upload REAL (multipart) de logo/favicon + remoção. Valida MIME/tamanho no
 * cliente (espelha o service) antes de enviar; exibe o asset corrente via
 * signed URL (logoUrl/faviconUrl). Desabilitado quando `canManage` é falso (D-B).
 */

import { useId, useRef, useState } from 'react';

import { validateAssetFile } from './branding-form';
import { ASSET_ACCEPT, MAX_ASSET_BYTES, type AssetKind } from './types';

interface AssetUploaderProps {
  kind: AssetKind;
  url: string | null;
  canManage: boolean;
  isBusy: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

export function AssetUploader({
  kind,
  url,
  canManage,
  isBusy,
  onUpload,
  onRemove,
}: AssetUploaderProps): JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const spec = ASSET_ACCEPT[kind];
  const hasAsset = url !== null;

  function pick(): void {
    if (!canManage || isBusy) return;
    inputRef.current?.click();
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>): void {
    setLocalError(null);
    const file = e.target.files?.[0];
    // Reset para permitir re-selecionar o mesmo arquivo depois.
    e.target.value = '';
    if (file === undefined) return;
    const err = validateAssetFile(file, spec.mimes, MAX_ASSET_BYTES);
    if (err !== null) {
      setLocalError(err);
      return;
    }
    onUpload(file);
  }

  return (
    <div className="brand-asset">
      <div
        className={`brand-asset__zone${hasAsset ? ' brand-asset__zone--filled' : ''}`}
        onClick={pick}
        role="button"
        tabIndex={canManage ? 0 : -1}
        aria-disabled={!canManage}
        aria-label={`${kind === 'logo' ? 'Logo' : 'Favicon'}: ${hasAsset ? 'substituir' : 'enviar'}`}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            pick();
          }
        }}
      >
        {hasAsset ? (
          <img className="brand-asset__preview" src={url} alt={`${kind} atual`} />
        ) : (
          <span className="brand-asset__empty">
            {canManage
              ? 'Clique para enviar'
              : `${kind === 'logo' ? 'Logo' : 'Favicon'} não definido`}
          </span>
        )}
      </div>

      <div className="brand-asset__meta">
        <span className="brand-asset__hint">{spec.hint}</span>
        <div className="brand-asset__actions">
          <button
            type="button"
            className="brand-btn brand-btn--sm"
            disabled={!canManage || isBusy}
            onClick={pick}
          >
            {hasAsset ? 'Substituir' : 'Enviar'}
          </button>
          {hasAsset && (
            <button
              type="button"
              className="brand-btn brand-btn--sm brand-btn--ghost"
              disabled={!canManage || isBusy}
              onClick={onRemove}
            >
              Remover
            </button>
          )}
        </div>
        {localError !== null && (
          <span className="brand-asset__error" role="alert">
            {localError}
          </span>
        )}
      </div>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={spec.accept}
        hidden
        onChange={handleFile}
      />
    </div>
  );
}
